import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getColorScheme } from '../theme/colorSchemes';

/**
 * Reviews panel — opens from the rating badge.
 *
 * Reads GET /reviews/:expertId on widget-server, which returns a page of reviews
 * plus a summary. The summary always covers ALL public reviews, never the active
 * filter, so filtering to 4★ doesn't make the headline average read 4.0.
 */

const LANGUAGE_NAMES = {
  en: 'English', fr: 'Français', es: 'Español', pt: 'Português', ru: 'Русский',
  ar: 'العربية', hi: 'हिन्दी', pa: 'ਪੰਜਾਬੀ', zh: '中文', fa: 'فارسی', uk: 'Українська',
  tr: 'Türkçe', ta: 'தமிழ்', ur: 'اردو', vi: 'Tiếng Việt', ko: '한국어', ro: 'Română',
  he: 'עברית', tl: 'Tagalog', bn: 'বাংলা'
};

const Overlay = styled.div`
  position: absolute; inset: 0; z-index: 60;
  display: flex; align-items: flex-end; justify-content: center;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  animation: fadeIn 0.15s ease;
  @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
`;

const Sheet = styled.div`
  width: 100%; max-height: 92%;
  display: flex; flex-direction: column;
  background: #fff;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -8px 40px rgba(0,0,0,0.25);
  overflow: hidden;
  animation: slideUp 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  @keyframes slideUp { from { transform: translateY(16px); opacity: 0.6 } to { transform: none; opacity: 1 } }
`;

const Header = styled.div`
  padding: 14px 16px 10px;
  border-bottom: 1px solid #eef2f7;
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
`;

const Title = styled.h3`
  margin: 0; font-size: 15px; font-weight: 700; color: #0f172a;
`;

const CloseBtn = styled.button`
  border: none; background: none; cursor: pointer; padding: 4px;
  font-size: 20px; line-height: 1; color: #94a3b8;
  &:hover { color: #0f172a; }
`;

const SummaryRow = styled.div`
  display: flex; gap: 16px; padding: 12px 16px; align-items: center;
  border-bottom: 1px solid #eef2f7;
`;

const BigScore = styled.div`
  font-size: 34px; font-weight: 800; line-height: 1; color: #0f172a;
`;

const SummaryMeta = styled.div`
  font-size: 12px; color: #64748b; margin-top: 4px;
`;

const Bars = styled.div`
  flex: 1; display: flex; flex-direction: column; gap: 3px; min-width: 0;
`;

const BarRow = styled.button`
  display: flex; align-items: center; gap: 6px;
  border: none; background: none; padding: 0; cursor: pointer; font-family: inherit;
  opacity: ${p => (p.$dimmed ? 0.4 : 1)};
  &:hover { opacity: 1; }
`;

const BarLabel = styled.span`
  font-size: 11px; color: #64748b; width: 10px; text-align: right;
`;

const BarTrack = styled.span`
  flex: 1; height: 6px; border-radius: 3px; background: #eef2f7; overflow: hidden;
`;

const BarFill = styled.span`
  display: block; height: 100%; border-radius: 3px; background: #f59e0b;
  width: ${p => p.$pct}%;
`;

const BarCount = styled.span`
  font-size: 11px; color: #94a3b8; width: 26px; text-align: right;
`;

const Filters = styled.div`
  display: flex; gap: 6px; padding: 10px 16px; overflow-x: auto;
  border-bottom: 1px solid #eef2f7;
  &::-webkit-scrollbar { height: 0; }
`;

const Chip = styled.button`
  flex: 0 0 auto; padding: 4px 10px; border-radius: 999px; cursor: pointer;
  font-size: 12px; font-family: inherit; white-space: nowrap;
  border: 1px solid ${p => (p.$active ? getColorScheme(p.$scheme).primary : '#e2e8f0')};
  background: ${p => (p.$active ? getColorScheme(p.$scheme).primary : '#fff')};
  color: ${p => (p.$active ? '#fff' : '#475569')};
`;

const List = styled.div`
  flex: 1; overflow-y: auto; padding: 4px 16px 16px;
`;

const Card = styled.div`
  padding: 14px 0; border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
`;

const CardHead = styled.div`
  display: flex; align-items: center; gap: 8px; margin-bottom: 6px;
`;

const Avatar = styled.div`
  width: 30px; height: 30px; border-radius: 50%; flex: 0 0 auto;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: #fff;
  background: ${p => p.$bg};
`;

const AuthorName = styled.div`
  font-size: 13px; font-weight: 600; color: #0f172a;
`;

const AuthorMeta = styled.div`
  font-size: 11px; color: #94a3b8;
`;

const VerifiedTag = styled.span`
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 10px; font-weight: 600; color: #059669;
  background: #ecfdf5; border-radius: 4px; padding: 2px 5px;
`;

/** Provenance for reviews that came from somewhere other than our own form. */
const SourceTag = styled.span`
  display: inline-flex; align-items: center;
  font-size: 10px; font-weight: 600; color: #475569;
  background: #f1f5f9; border-radius: 4px; padding: 2px 5px;
`;

const Footnote = styled.span`
  font-size: 10px; color: #94a3b8; margin-left: 6px;
`;

const MiniStars = styled.div`
  font-size: 12px; color: #f59e0b; letter-spacing: 1px;
`;

const RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur']);

const ReviewText = styled.p`
  margin: 6px 0 0; font-size: 13px; line-height: 1.55; color: #334155;
  white-space: pre-wrap; word-break: break-word;
  direction: ${p => (p.$rtl ? 'rtl' : 'ltr')};
  text-align: ${p => (p.$rtl ? 'right' : 'left')};
`;

const Reply = styled.div`
  margin-top: 8px; padding: 8px 10px; border-radius: 8px;
  background: #f8fafc; border-left: 2px solid #cbd5e1;
  font-size: 12px; color: #475569; line-height: 1.5;
`;

const Empty = styled.div`
  padding: 32px 16px; text-align: center; font-size: 13px; color: #94a3b8;
`;

/** Shown only when the API reports it is serving simulation fixtures. */
const SampleBanner = styled.div`
  padding: 7px 16px;
  background: #fef3c7;
  border-bottom: 1px solid #fde68a;
  font-size: 11px; font-weight: 600; color: #92400e;
  text-align: center; letter-spacing: 0.02em;
`;

const MoreBtn = styled.button`
  width: 100%; margin-top: 12px; padding: 9px; cursor: pointer;
  border: 1px solid #e2e8f0; border-radius: 8px; background: #fff;
  font-size: 13px; font-family: inherit; color: #475569;
  &:hover { background: #f8fafc; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const AVATAR_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6'];
const avatarColor = (name) => {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const ReviewsPanel = ({ expertId, expertName, scheme, onClose }) => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [starFilter, setStarFilter] = useState(null);
  const [langFilter, setLangFilter] = useState(null);
  const [isSampleData, setIsSampleData] = useState(false);

  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const load = useCallback(async (targetPage, append) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(targetPage), limit: '10' });
      if (starFilter) params.set('rating', String(starFilter));
      if (langFilter) params.set('language', langFilter);

      const res = await fetch(`${baseUrl}/reviews/${encodeURIComponent(expertId)}?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setReviews(prev => (append ? [...prev, ...(data.reviews || [])] : data.reviews || []));
      setSummary(data.summary || null);
      setTotalPages(data.totalPages || 1);
      setPage(data.page || targetPage);
      setIsSampleData(!!data.sampleData);
    } catch (err) {
      console.error('[Reviews] load failed', err);
      setError('Could not load reviews right now.');
    } finally {
      setLoading(false);
    }
  }, [baseUrl, expertId, starFilter, langFilter]);

  // Filters reset back to page 1; the effect re-runs whenever `load` changes.
  useEffect(() => { load(1, false); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const total = summary?.total || 0;
  const maxBar = summary
    ? Math.max(...Object.values(summary.distribution || {}).map(Number), 1)
    : 1;

  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={e => e.stopPropagation()} role="dialog" aria-label={`Reviews for ${expertName}`}>
        <Header>
          <div>
            <Title>Client reviews</Title>
            <SummaryMeta>{expertName}</SummaryMeta>
          </div>
          <CloseBtn onClick={onClose} aria-label="Close reviews">×</CloseBtn>
        </Header>

        {isSampleData && (
          <SampleBanner>SIMULATED DATA — sample rows, not real client reviews</SampleBanner>
        )}

        {summary && (
          <SummaryRow>
            <div style={{ textAlign: 'center', flex: '0 0 auto' }}>
              <BigScore>{(summary.average || 0).toFixed(1)}</BigScore>
              <MiniStars>{'★'.repeat(Math.round(summary.average || 0))}</MiniStars>
              <SummaryMeta>{total} review{total === 1 ? '' : 's'}</SummaryMeta>
              {summary.verifiedCount > 0 && (
                <SummaryMeta style={{ color: '#059669', fontWeight: 600 }}>
                  {summary.verifiedCount} verified
                </SummaryMeta>
              )}
            </div>
            <Bars>
              {[5, 4, 3, 2, 1].map(star => {
                const count = Number(summary.distribution?.[star] || 0);
                return (
                  <BarRow
                    key={star}
                    $dimmed={starFilter !== null && starFilter !== star}
                    onClick={() => setStarFilter(starFilter === star ? null : star)}
                    aria-label={`${count} ${star}-star reviews`}
                  >
                    <BarLabel>{star}</BarLabel>
                    <BarTrack><BarFill $pct={(count / maxBar) * 100} /></BarTrack>
                    <BarCount>{count}</BarCount>
                  </BarRow>
                );
              })}
            </Bars>
          </SummaryRow>
        )}

        {summary?.languages?.length > 1 && (
          <Filters>
            <Chip $scheme={scheme} $active={!langFilter} onClick={() => setLangFilter(null)}>
              All languages
            </Chip>
            {summary.languages.map(l => (
              <Chip
                key={l.language}
                $scheme={scheme}
                $active={langFilter === l.language}
                onClick={() => setLangFilter(langFilter === l.language ? null : l.language)}
              >
                {LANGUAGE_NAMES[l.language] || l.language.toUpperCase()} ({l.count})
              </Chip>
            ))}
          </Filters>
        )}

        <List>
          {error && <Empty>{error}</Empty>}
          {!error && !loading && reviews.length === 0 && (
            <Empty>No reviews match that filter yet.</Empty>
          )}

          {reviews.map(r => (
            <Card key={r.id}>
              <CardHead>
                <Avatar $bg={avatarColor(r.author)}>
                  {(r.author || '?').charAt(0).toUpperCase()}
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <AuthorName>{r.author}</AuthorName>
                  <AuthorMeta>
                    {[r.country, formatDate(r.publishedAt)].filter(Boolean).join(' · ')}
                  </AuthorMeta>
                </div>
                {r.verified && <VerifiedTag>✓ Verified client</VerifiedTag>}
                {!r.verified && r.source === 'imported' && <SourceTag>via Google</SourceTag>}
              </CardHead>

              <MiniStars aria-label={`${r.rating} out of 5`}>
                {'★'.repeat(Math.round(r.rating))}
                <span style={{ color: '#e2e8f0' }}>{'★'.repeat(5 - Math.round(r.rating))}</span>
              </MiniStars>

              {r.title && (
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginTop: 4 }}>
                  {r.title}
                </div>
              )}
              {r.text ? (
                <ReviewText
                  $rtl={RTL_LANGUAGES.has(String(r.language || '').split('-')[0])}
                  lang={r.language}
                >
                  {r.text}
                  {r.textTruncated && <Footnote>… (shortened)</Footnote>}
                </ReviewText>
              ) : (
                // A rating with no written body — say so rather than rendering a
                // blank card that looks like a loading failure.
                <Footnote style={{ marginLeft: 0, fontStyle: 'italic' }}>
                  Rated without a written review
                </Footnote>
              )}
              {r.translatedFrom && (
                <Footnote style={{ marginLeft: 0 }}>
                  Translated from {LANGUAGE_NAMES[r.translatedFrom] || r.translatedFrom}
                </Footnote>
              )}

              {r.adviserReply && (
                <Reply>
                  <strong>Response from {expertName}:</strong> {r.adviserReply}
                </Reply>
              )}
            </Card>
          ))}

          {loading && <Empty>Loading…</Empty>}

          {!loading && page < totalPages && (
            <MoreBtn onClick={() => load(page + 1, true)} disabled={loading}>
              Show more reviews
            </MoreBtn>
          )}
        </List>
      </Sheet>
    </Overlay>
  );
};

export default ReviewsPanel;
